import { useMultiStyleConfig, forwardRef } from '@chakra-ui/react';
import { cx } from '@chakra-ui/utils';
import { useMemo } from 'react';

import { Box, type BoxProps } from '@frontend/components';

import { type TableStyles, TableStylesProvider } from '../TableStylesContext';
import { TableProvider } from '../utils/context';
import { getClassName } from './className';

export type TableContainerProps = BoxProps & {
  size?: string;
  variant?: string;
  zebra?: 'even' | 'odd';
  density?: 'relaxed' | 'normal';
  zebraIndex?: number;
};

export const TableContainer = forwardRef<TableContainerProps, 'div'>(
  (
    {
      __css: __cssProp,
      size,
      variant,
      children,
      zebra,
      density = 'normal',
      className,
      ...restProps
    },
    ref,
  ) => {
    const tableStyles = useMultiStyleConfig('Table', {
      size,
      variant,
    }) as TableStyles;

    const __css = useMemo(
      () => ({ ...tableStyles.container, ...__cssProp }),
      [tableStyles.container, __cssProp],
    );

    const tableContextValue = useMemo(
      () => ({ size, variant, zebra, density }),
      [size, variant, zebra, density],
    );

    return (
      <TableStylesProvider value={tableStyles}>
        <Box
          ref={ref}
          __css={__css}
          className={cx(getClassName('container'), className)}
          {...restProps}
        >
          <TableProvider value={tableContextValue}>{children}</TableProvider>
        </Box>
      </TableStylesProvider>
    );
  },
);
TableContainer.displayName = 'TableContainer';
